import * as React from "react";

import { ContentSection } from "../Layout/ContentSection";
import { RLink } from "../DesignSystem/RLink";
import { Heading } from "../DesignSystem/formatting/Heading";
import { Subheading } from "../DesignSystem/formatting/Subheading";
import { Paragraph } from "../DesignSystem/formatting/Paragraph";

export const ExplainChipsPage = (): JSX.Element => {
  return (
    <div>
      <ContentSection>
        <Heading>What is Chips Compo?</Heading>

        <Subheading>
          No one ever reads this. I only wrote it down so that if someone breaks
          the rules I can point here.
        </Subheading>
      </ContentSection>

      <ContentSection>
        <Paragraph>
          Chips Compo is about helping musicians improve by giving them guided
          feedback and encouragement. We do that by running anonymous contests
          every week to have fun and improve.
        </Paragraph>

        <h2>Compos</h2>

        <Paragraph>There are two types of compos.</Paragraph>

        <Paragraph>
          <strong>Chips Compos</strong> are week-long competitions, held every
          other week. These are the relatively more serious of the two types,
          and everyone's encouraged to try their best!
        </Paragraph>

        <Paragraph>
          <strong>Salsa Compos</strong> are held in the off-weeks during chips
          voting, and are generally shorter (a few days long at most), less
          serious, more memey, and more experimental, with sometimes ridiculous
          or tricky prompts. Joke tracks are somewhat discouraged in Chips
          Compos, but are welcomed in Salsa Compos.
        </Paragraph>

        <h3>Voting</h3>

        <Paragraph>
          After the submission period, there will be a judging period where
          everyone votes on the contest entries. Everyone will receive a
          randomly selected group of 20 or fewer entries to vote on.
        </Paragraph>

        <Paragraph>
          <strong>If you participate in a Chips Compo, you must vote!</strong>{" "}
          If you don't vote, you will not receive your own results, unless you
          happened to place in the top 7.
        </Paragraph>

        <h3>Results</h3>

        <Paragraph>
          After voting, results are announced! Results will{" "}
          <RLink to="https://discord.gg/AUvDuUkxXr">
            almost always be announced on Discord first
          </RLink>
          , so it's wise to hop into Discord at the end of judging, as a results
          stream is always fun! :D
        </Paragraph>

        <h3>Anonymity</h3>

        <Paragraph>
          All songs on this site are anonymous until results are released. This
          is to make voting more fair, as everyone is hopefully voting purely on
          the merit of the song. It also encourages authors to continue to be
          creative and try new styles in order to throw off guessers. :)
        </Paragraph>

        <Paragraph>
          You are not <em>required</em> to go anonymous! It's merely a benefit
          Chips provides. You will never be punished on Chips for being obvious
          about who you are.
        </Paragraph>

        <h2>XP and Gold</h2>

        <Paragraph>
          As you participate in contests, you'll begin accumulating XP and Gold!
        </Paragraph>

        <Paragraph>
          <strong>XP</strong> is like "songwriting experience," and you earn it
          from writing good songs. As you gain XP, you'll become a more
          influential voter. Once you cross 5000 XP, you'll become a part of the{" "}
          <em>elite</em> Salsa Compo Planning Committee!
        </Paragraph>

        <Paragraph>
          <strong>Gold</strong> is earned by helping around in the community,
          giving good feedback and voting. You can spend it to submit more
          songs, and get more people to check out your stuff.
        </Paragraph>

        <h2>Rules</h2>

        <h3>Alt Accounts</h3>

        <Paragraph>
          Alt accounts are permissible{" "}
          <em>
            so long as you tell johnfn (or another chips moderator) in advance!
          </em>{" "}
          If you don't, johnfn might steal all your <s>trophies</s> chips and
          put them in his profile instead. (Yes... it's very serious!!)
        </Paragraph>

        <Paragraph>
          The main problems with alt accounts are when people use them to:
        </Paragraph>

        <ul>
          <li>Vote on tracks twice (or voting on that user's own tracks)</li>
          <li>Vote on themes twice</li>
          <li>
            Bypass the 10 gold cost for submitting additional songs to a compo.
          </li>
        </ul>

        <Paragraph>
          Using alt accounts to do either of those things is a serious problem
          and possibly one of the only things johnfn may actually consider
          banning someone for.
        </Paragraph>

        <h3>Starting early</h3>

        <Paragraph>
          Unless the rules specify otherwise, you must not have started your
          piece before the start of the compo! It must have been created
          entirely from scratch during the compo.
        </Paragraph>

        <h3>Themes</h3>

        <Paragraph>
          As fun as themes are, we never <em>enforce</em> a theme fit, as some
          people will use a theme as a starting point but end up somewhere
          totally different. (johnfn is sympathetic as he does this all the
          time.)
        </Paragraph>

        <h3>Be nice</h3>

        <Paragraph>
          <em>Even if it's true</em>, saying things like "this is a bad song" or
          "this is lazy" will never be tolerated on Chips. Chips is a supportive
          community that wants everyone to get better, and outright criticism
          does not foster supportiveness. It is always better to say{" "}
          <em>how you would improve a song</em>.
        </Paragraph>

        <Paragraph>
          Similarly, things like "this didn't deserve [whatever score or
          placement]" are not tolerated. These kind of comments create drama and
          bad feelings all around. Again, if you don't like something, say how
          you would improve the song.
        </Paragraph>

        <h3>Sampling</h3>

        <Paragraph>
          Sampling other songs in your song is permissible, so long as the
          result is original and/or creative. As long as you're cutting up and
          slicing the samples in a way that doesn't resemble the original song,
          you are fine. If you're not, or if the original song is recognizeable,
          you're walking a fine line and you may want to contact johnfn or
          another moderator to explain what you're trying to do.
        </Paragraph>

        <Paragraph>
          Sample packs are always fine (unless the compo rules say otherwise).
        </Paragraph>

        <h3>Vocal samples</h3>

        <Paragraph>
            Directly using vocal melodies or acapellas is not acceptable (you
            should be coming up with the melodies yourself). However, asking
            someone else to sing a vocal line, OR, chopping up the vocals (or
            repitching them) into new melodies is perfectly acceptable!
          </Paragraph>

          <h3>Theme</h3>

          <Paragraph>
            I am particularly sympathetic to people who are incapable of
            following themes, because I am exactly the same way. Please try not
            to factor theme fit into overall score; score the song soley on much
            you like it. If you must, take it out in the theme category.
          </Paragraph>

        <h2>
          <RLink to="/edit/profile">Go Back.</RLink>
        </h2>
      </ContentSection>
    </div>
  );
};
